import { Dayjs, isDayjs } from 'dayjs';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  QueryOptions,
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from 'react-query';
import BaseService from 'services/api/BaseService';

export type SearchParams = Record<
  string,
  string | string[] | Date | Dayjs | number | boolean | undefined | null
>;

export type UseApiResult<ReturnType> = UseQueryResult<ReturnType, Error> & {
  invalidateQuery: () => void;
};

export type UseApiMutationResult<InputType, ReturnType> = UseMutationResult<
  ReturnType,
  Error,
  InputType
>;

export interface ApiOptions {
  key?: any[];
  method?: string;
  isJson?: boolean;
  requiredAuth?: boolean;
  body?: object | string;
  isMultipart?: boolean;
  searchParams?: SearchParams;
  staleTime?: number;
  enabled?: boolean;
  retry?: QueryOptions['retry'];
}

function serializeQueryStringValue(value: any): string {
  return isDayjs(value)
    ? value.toISOString()
    : typeof value === 'object' && value instanceof Date
      ? value.toISOString()
      : `${value}`;
}

function getQueryString(searchParams?: SearchParams): string {
  const searchParamsInstance = new URLSearchParams();
  if (searchParams) {
    for (const key of Object.keys(searchParams)) {
      const value = searchParams[key];
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          value.forEach((arrayValue) =>
            searchParamsInstance.append(
              key,
              serializeQueryStringValue(arrayValue),
            ),
          );
        } else {
          searchParamsInstance.append(key, serializeQueryStringValue(value));
        }
      }
    }
  }
  return searchParamsInstance.size ? `?${searchParamsInstance}` : '';
}

export function useApi<ReturnType>(
  url: string | null,
  {
    key,
    method,
    isJson = true,
    requiredAuth = true,
    body,
    isMultipart = false,
    searchParams,
    staleTime = Infinity,
    enabled = true,
    retry,
  }: ApiOptions = {},
): UseApiResult<ReturnType> {
  const { t } = useTranslation();
  const queryString = getQueryString(searchParams);
  const queryClient = useQueryClient();
  const queryKey = key || [url, queryString];

  const invalidateQuery = useCallback(() => {
    const invalidateQueryKey = key
      ? Array.isArray(key)
        ? key[0]
        : key
      : [url];
    queryClient.invalidateQueries(invalidateQueryKey);
  }, [key, queryClient, url]);

  const useQueryResult = useQuery<ReturnType, Error>({
    queryKey,
    staleTime,
    enabled,
    retry,
    queryFn: async () => {
      try {
        if (!url) {
          return undefined;
        }
        const response = await BaseService.request(
          `${url}${queryString}`,
          method ?? 'GET',
          requiredAuth,
          body,
          isMultipart,
        );
        if (isJson && typeof response.json !== 'function') {
          throw new Error(t('global:internet_connexion_error'));
        }
        const data = isJson ? await response.json() : response;
        if ([200, 201].includes(response.status)) {
          return data;
        }
        throw new Error(data?.message || t('global:internet_connexion_error'));
      } catch (e: any) {
        console.warn('useApi error', e);
        throw e;
      }
    },
  });

  return useMemo(
    () => ({ invalidateQuery, ...useQueryResult }),
    [invalidateQuery, useQueryResult],
  );
}

export function useApiMutation<InputType, ReturnType>(
  url: string | null,
  {
    method,
    isJson = true,
    requiredAuth = true,
    isMultipart = false,
    searchParams,
  }: ApiOptions = {},
): UseApiMutationResult<InputType, ReturnType> {
  const { t } = useTranslation();
  const queryString = getQueryString(searchParams);
  return useMutation<ReturnType, Error, InputType>(async (body) => {
    try {
      if (!url) {
        return undefined;
      }
      const response = await BaseService.request(
        `${url}${queryString}`,
        method ?? 'POST',
        requiredAuth,
        body,
        isMultipart,
      );
      const data = isJson ? await response.json() : response;
      if ([200, 201].includes(response.status)) {
        return data;
      }
      throw new Error(data?.message || t('global:internet_connexion_error'));
    } catch (e: any) {
      console.warn('useApiMutation error', e);
      throw e;
    }
  });
}

export default useApi;
